import {
  LINK_METADATA_ACCOUNT_ID_FIELD_OPTION,
  LINK_METADATA_USER_ID_FIELD_OPTION,
} from '@modules/integrationsV2/constants';
import { MetadataFieldRelationByIntegrationType } from '@modules/integrationsV2/store/types';
import { IntegrationSource } from '@modules/integrationsV2/types';
import { IntegrationType } from 'generated/graphql';

interface GetExistingFieldsRelationParams {
  fieldRelationsByType: MetadataFieldRelationByIntegrationType[];
  sourceIntegrationSource: IntegrationSource;
  targetIntegrationSource: IntegrationSource;
}

// temporary, will remove it once BE handles ID fields from their end
const getRelatedSourceAndTargetFields = (
  match: MetadataFieldRelationByIntegrationType | undefined,
  sourceIntegrationSource: IntegrationSource,
  targetIntegrationSource: IntegrationSource,
) => {
  return {
    relatedSourceField: match?.[sourceIntegrationSource.integrationType]?.field,
    relatedTargetField: match?.[targetIntegrationSource.integrationType]?.field,
  };
};

export const getExistingFieldsRelation = ({
  fieldRelationsByType,
  sourceIntegrationSource,
  targetIntegrationSource,
}: GetExistingFieldsRelationParams) => {
  const directMatchRelation = fieldRelationsByType.find((fieldRelation) => {
    const sourceFieldRelation =
      fieldRelation[sourceIntegrationSource.integrationType];

    const targetFieldRelation =
      fieldRelation[targetIntegrationSource.integrationType];

    const areTypesPresent = !!sourceFieldRelation && !!targetFieldRelation;

    const areSourcesMatching =
      sourceFieldRelation?.source.toLowerCase() ===
        sourceIntegrationSource.source?.toLowerCase() &&
      targetFieldRelation?.source.toLowerCase() ===
        targetIntegrationSource.source?.toLowerCase();

    return areTypesPresent && areSourcesMatching;
  });

  if (directMatchRelation) {
    return getRelatedSourceAndTargetFields(
      directMatchRelation,
      sourceIntegrationSource,
      targetIntegrationSource,
    );
  }

  const idMatchRelationAgainstSource =
    targetIntegrationSource.integrationType !== IntegrationType.Feedback &&
    fieldRelationsByType.find((fieldRelation) => {
      const sourceFieldRelation =
        fieldRelation[sourceIntegrationSource.integrationType];

      const targetFieldRelation =
        fieldRelation[targetIntegrationSource.integrationType];

      const areTypesPresent = !!sourceFieldRelation && !!targetFieldRelation;

      const isSourceMatching =
        !!sourceFieldRelation?.source &&
        sourceFieldRelation?.source.toLowerCase() ===
          sourceIntegrationSource.source?.toLowerCase();

      const isTargetIdField =
        targetIntegrationSource.integrationType === IntegrationType.User
          ? targetFieldRelation?.field ===
            LINK_METADATA_USER_ID_FIELD_OPTION.field
          : targetFieldRelation?.field ===
            LINK_METADATA_ACCOUNT_ID_FIELD_OPTION.field;

      return areTypesPresent && isSourceMatching && isTargetIdField;
    });

  if (idMatchRelationAgainstSource) {
    return getRelatedSourceAndTargetFields(
      idMatchRelationAgainstSource,
      sourceIntegrationSource,
      targetIntegrationSource,
    );
  }

  const idMatchRelationAgainstTarget =
    sourceIntegrationSource.integrationType !== IntegrationType.Feedback &&
    fieldRelationsByType.find((fieldRelation) => {
      const sourceFieldRelation =
        fieldRelation[sourceIntegrationSource.integrationType];

      const targetFieldRelation =
        fieldRelation[targetIntegrationSource.integrationType];

      const areTypesPresent = !!sourceFieldRelation && !!targetFieldRelation;

      const isTargetMatching =
        !!targetFieldRelation?.source &&
        targetFieldRelation?.source.toLowerCase() ===
          targetIntegrationSource.source?.toLowerCase();

      const isSourceIdField =
        sourceIntegrationSource.integrationType === IntegrationType.User
          ? sourceFieldRelation?.field ===
            LINK_METADATA_USER_ID_FIELD_OPTION.field
          : sourceFieldRelation?.field ===
            LINK_METADATA_ACCOUNT_ID_FIELD_OPTION.field;

      return areTypesPresent && isTargetMatching && isSourceIdField;
    });

  if (idMatchRelationAgainstTarget) {
    return getRelatedSourceAndTargetFields(
      idMatchRelationAgainstTarget,
      sourceIntegrationSource,
      targetIntegrationSource,
    );
  }

  const idMatchRelationForBoth =
    sourceIntegrationSource.integrationType !== IntegrationType.Feedback &&
    targetIntegrationSource.integrationType !== IntegrationType.Feedback &&
    fieldRelationsByType.find((fieldRelation) => {
      const sourceFieldRelation =
        fieldRelation[sourceIntegrationSource.integrationType];

      const targetFieldRelation =
        fieldRelation[targetIntegrationSource.integrationType];

      const areTypesPresent = !!sourceFieldRelation && !!targetFieldRelation;

      const isSourceIdField =
        sourceIntegrationSource.integrationType === IntegrationType.User
          ? sourceFieldRelation?.field ===
            LINK_METADATA_USER_ID_FIELD_OPTION.field
          : sourceFieldRelation?.field ===
            LINK_METADATA_ACCOUNT_ID_FIELD_OPTION.field;

      const isTargetIdField =
        targetIntegrationSource.integrationType === IntegrationType.User
          ? targetFieldRelation?.field ===
            LINK_METADATA_USER_ID_FIELD_OPTION.field
          : targetFieldRelation?.field ===
            LINK_METADATA_ACCOUNT_ID_FIELD_OPTION.field;

      return areTypesPresent && isSourceIdField && isTargetIdField;
    });

  if (idMatchRelationForBoth) {
    return getRelatedSourceAndTargetFields(
      idMatchRelationForBoth,
      sourceIntegrationSource,
      targetIntegrationSource,
    );
  }

  return {
    relatedSourceField: undefined,
    relatedTargetField: undefined,
  };
};
